import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnChanges,
    ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
    SeConstraintApplierService,
    SeConstraintConfiguratorService,
    SeConstraintNotifierService,
} from '@se/constraint';
import { IDataSourceItem } from '@se/cp.common';
import { Logger, ModalDialogNotificationService, NotificationType } from '@se/se.ui.html5';
import { ItemNode, SelectorStatus } from '@se/ui.selector';
import { firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CpEntityList, CpSelectorControl } from '../../cp-configuration/view-model';
import { CpModelService } from '../../cp-model.service';
import { CpDatasourceService } from '../../cp-datasource.service';
import { AbstractControlComponent } from '../abstract-control';
import { flatten, uniq } from 'lodash-es';

export const findItemNodeById = (itemNodes: ItemNode[], id: string): ItemNode | undefined => {
    for (const itemNode of itemNodes) {
        if (itemNode.Id === id || itemNode.id === id) {
            return itemNode;
        }
        if (itemNode.children) {
            const foundItemNode = findItemNodeById(itemNode.children, id);
            if (foundItemNode) {
                return foundItemNode;
            }
        }
    }
    return undefined;
};

export const isItemNode = (item: any): item is ItemNode => {
    return item.id && item.Id && item.id === item.Id && item instanceof ItemNode;
};

export const isListOfItemNode = (item: any): item is ItemNode[] => Array.isArray(item) && item.every(isItemNode);

@Component({
    selector: 'cp-selector',
    templateUrl: './selector.component.html',
    styleUrls: ['./selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpSelectorComponent extends AbstractControlComponent implements AfterViewInit, OnChanges {
    @ViewChild('cpSelector', { read: ElementRef, static: true }) selectorEl!: ElementRef<HTMLElement>;

    control!: CpSelectorControl;
    dataSource: ItemNode[] = [];
    dataSourceIds: string[] = [];
    model: IDataSourceItem[] = [];
    filterText = '';
    status: SelectorStatus = 'view';

    constructor(
        private datasourceService: CpDatasourceService,
        private modelService: CpModelService,
        private configuratorService: SeConstraintConfiguratorService,
        private constraintFilterService: SeConstraintApplierService,
        cdr: ChangeDetectorRef,
        constraintNotifier: SeConstraintNotifierService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private translate: TranslateService,
        logger: Logger,
    ) {
        super(cdr, constraintNotifier, logger);
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
        this.dataBind();
    }

    ngOnChanges() {
        this.status = this.isDisabled ? 'view' : 'edit';
    }

    init() {
        if (!this.control.dataSource) {
            this.logger?.error(`(${this.control.name}) No dataSource configured for selector.`);
        }

        this.control
            .getEntityObservable(this.modelService)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(this._entitySubject);

        const initialValueRaw = this.control.getInitialValue(this.modelService) as any;
        // Se il dato iniziale è nullo, facciamo un update ad array vuoto senza emettere eventi, altrimenti al primo
        //   update automatico del controllo, questo risulta modificato.
        if (!initialValueRaw) {
            this.modelService.setValue(this.control.dataBinding, [], { emitEvent: false });
        }
        // TODO: Non convertire ogni volta.
        const initialValue = initialValueRaw?.toJSON() || [];
        for (const item of initialValue) {
            item.Description = item.Path || item.Description;
        }
        this.formControl = new FormControl(initialValue, [
            this.makeValidator(this._entitySubject, this.control.dataBinding.propertyName, this.control.required),
        ]);

        this.dataSource =
            (this.datasourceService.getById(this.control.dataSource || '', initialValue) as unknown as ItemNode[]) ??
            [];
        this.dataSourceIds = this.getAllIdValueFromObject(this.dataSource);

        const constraints = uniq(
            flatten(
                this.configuratorService.constraintSources
                    .getSourcesByListName(this.control.dataSource ?? '')
                    .map((s) => s.constraints),
            ),
        );
        const dataBindingContext = 'dataBinding' in this.control ? this.control.dataBinding.toString() : undefined;

        for (const c of constraints) {
            this.constraintNotifier
                .register(c.id, dataBindingContext)
                .pipe(takeUntil(this.destroyed$))
                .subscribe((_) => this.handleFilterNotification(c.id));
        }
    }

    protected handleFilterNotification(constraintId: string) {
        this.entity?.select();
        this.dataSource = this.constraintFilterService.applyFilter(constraintId) as unknown as ItemNode[];
        this.dataSourceIds = this.getAllIdValueFromObject(this.dataSource);
    }

    onClickOutside() {
        this.filterText = '';
        this.status = 'view';
    }

    onClickInside(_: Event) {
        if (!this.isDisabled) {
            this.focusin.emit();
        }
    }

    onModelChange(model: any[]) {
        let cleanModel = model || [];

        if (!isListOfItemNode(cleanModel)) {
            return false;
        }

        cleanModel = cleanModel.map((m) => findItemNodeById(this.dataSource, m.id)).filter(Boolean);

        const entity = this.modelService.makeEntity(cleanModel, this.control.dataBinding);
        return this.control.onModelChange(this.modelService, entity, !this.isDisabled);
    }

    onMaxSelectableItems(_: any) {}

    getAllIdValueFromObject(object: any): string[] {
        const ids: any = [];
        JSON.stringify(object, (key, value) => {
            if (key === 'Id') {
                ids.push(value);
            }
            return value;
        });
        return ids;
    }

    /**
     *  se il valore in model non è in initial values allora controllo che ci sia in datasource (ricorsivamente)
     *  una volta che faccio vedere la popup perchè un valore non è nel datasource, lo aggiungo ai initial values..
     */
    private countRecordsNotFound(model: IDataSourceItem[]) {
        const selectable = [];
        const notSelectable = [];

        for (const value of model) {
            if (this.dataSourceIds.indexOf(value.Id) <= -1) {
                notSelectable.push(value);
            } else {
                selectable.push(value);
            }
        }
        return [selectable, notSelectable];
    }

    private dataBind() {
        // Databinding effettivo del modello.
        this.control
            .getModelObservable(this.modelService)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((model) => {
                if (model instanceof CpEntityList) {
                    model = model.activeEntities;
                }

                const [selectable, notSelectable] = this.countRecordsNotFound(
                    (model as unknown as IDataSourceItem[]) ?? [],
                );

                this.formControl?.setValue(selectable);
                this.model = selectable;
                this.logger && this.logger.trace(`Model update ${this.control.name}`);

                this.showMissingItemModal(notSelectable.length);

                this.cdr.detectChanges();
            });
    }

    private showMissingItemModal(recordsNotFound: number) {
        if (recordsNotFound) {
            let message = this.translate.instant('PartiallyImportOnTotalSingular', { p0: recordsNotFound });
            if (recordsNotFound > 1) {
                message = this.translate.instant('PartiallyImportOnTotalPlural', { p0: recordsNotFound });
            }

            firstValueFrom(
                this.modalDialogNotificationService.notify({
                    buttonYes: false,
                    buttonNo: false,
                    buttonCancel: false,
                    buttonOk: true,
                    title: this.translate.instant('Information'),
                    message,
                    type: NotificationType.warning,
                }),
            );
        }
    }
}
